import type { MessageType, SmartOfferActions } from "@expert/shared-types";

import type { MultipleActionName, MultipleActions } from "./types";

const TSNR_URL = "https://csp-prod-eastus2-trip-mo.it.att.com/portal/trip_mo/Dashboard_MO";

export function useCreateActions({
    setIsExpanded,
    messageId,
    messageType,
    smartOfferActions,
}: {
    setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    messageId?: string;
    messageType?: MessageType;
    smartOfferActions?: SmartOfferActions;
}): Record<MultipleActionName, MultipleActions> {
    const eventData = { messageId, messageType };
    return {
        expandMessage: {
            value: "Show more",
            analytics: {
                actionId: "ExpandMessage",
                eventData,
            },
            onClick: () => {
                setIsExpanded(true);
            },
            isVisibleAfterClick: true,
        },
        neutralOrPositiveFeedback: {
            value: ["Issue resolved", "Still need help"],
            analytics: {
                actionId: "MessageFeedbackDetails",
                eventData,
            },
        },
        negativeFeedback: {
            value: "Wrong issue",
            analytics: {
                actionId: "MessageFeedbackDetails",
                eventData,
            },
        },
        tsnr: {
            value: "Open Trip instead",
            analytics: { actionId: "OpenTSNR", eventData },
            onClick: () => {
                window.open(TSNR_URL, "_blank", "noopener,noreferrer");
            },
            isVisibleAfterClick: true,
        },
        additionalPromptActions: {
            value: ["Simplify for beginners", "Give more detail", "Summarize"],
            analytics: {
                actionId: "AdditionalPromptAction",
                eventData,
            },
        },
        sendToCustomer: {
            value: "Send to customer",
            analytics: {
                actionId: "SendCustomerChatMessage",
                eventData,
            },
            onClick: async () => {
                if (smartOfferActions?.handleChatSendToCustomer) {
                    await smartOfferActions.handleChatSendToCustomer();
                }
            },
            isVisibleAfterClick: false,
        },
        sendEnrollment: {
            value: "Send enrollment",
            analytics: {
                actionId: "SendEnrollmentChatMessage",
                eventData,
            },
            onClick: async () => {
                if (smartOfferActions?.handleChatSendEnrollment) {
                    await smartOfferActions.handleChatSendEnrollment();
                }
            },
            isVisibleAfterClick: false,
        },
        declined: {
            value: "Declined",
            analytics: {
                actionId: "EnrollmentDeclineButton",
                eventData,
            },
            onClick: async () => {
                if (smartOfferActions?.handleChatDeclineOffer) {
                    await smartOfferActions.handleChatDeclineOffer();
                }
            },
            isVisibleAfterClick: false,
        },
        resendDisclosureLink: {
            value: "Re-send link",
            analytics: {
                actionId: "ResendDisclosureLink",
                eventData,
            },
            onClick: async () => {
                if (smartOfferActions?.handleResendDisclosureLink) {
                    await smartOfferActions.handleResendDisclosureLink();
                }
            },
            isVisibleAfterClick: true,
            loading: smartOfferActions?.isResendLinkLoading,
            disabled: smartOfferActions?.isMaxAttemptsReached,
        },
        previewDisclosures: {
            value: "Preview disclosures",
            analytics: {
                actionId: "PreviewDisclosure",
                eventData,
            },
            onClick: async () => {
                if (smartOfferActions?.handlePreviewDisclosures) {
                    await smartOfferActions.handlePreviewDisclosures();
                }
            },
            isVisibleAfterClick: true,
        },
    };
}
